// Payment claim arithmetic — the same method as the interim certificates (Staff Village cert 10):
// works + variations on account = total VEP; less deposit recovered this claim (a % of the contract deposit, until it is
// all back) = total claim; less retention (% of the total claim, until the limit) = total payable VEP; + VAT = VIP.
// Balance of contract = contract sum + variations on account to date − retention to date − deposit recovered to date
// − paid to date (VEP). A deposit (advance) claim (V0.4.6) pays the deposit itself: no works, no deposit recovery, and
// retention only when the PM ticks it; later claims recover that deposit. When a deposit claim is recorded, paid to date
// includes it, so only deposit recovered beyond the deposit paid is taken off the balance. Used by the claim page and the certificate PDF (no imports — shared by app and functions).

export type Terms = {
  sum_vep: number;
  deposit_pct: number; // e.g. 10
  deposit_recovery_pct: number; // % of the deposit recovered each claim, e.g. 4.16
  retention_pct: number; // e.g. 5
  retention_limit_pct: number; // limit as % of contract + approved variations, e.g. 5
  vat_pct?: number; // 12.5
};

export type ToDate = { gross_vep: number; variations_vep: number; deposit_recovered: number; retention_held: number; paid_vep: number; /** deposit paid through PM C3 deposit claims (VEP, before retention) */ deposit_paid?: number };

export type LineIn = { contract_value: number | null; prev_pct: number | null; claimed_pct: number | null; swi_pct: number | null };

export const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const n0 = (v: number | null | undefined) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Amount of a line this period: value × (% now − % before). */
export function lineAmounts(l: LineIn) {
  const v = n0(l.contract_value);
  const prev = n0(l.prev_pct);
  return {
    claimed: r2((v * (n0(l.claimed_pct) - prev)) / 100),
    swi: r2((v * ((l.swi_pct ?? l.claimed_pct ?? prev) - prev)) / 100),
  };
}

export type Assessment = {
  works: number;
  variations: number;
  totalVep: number;
  deposit: { full: number; rate: number; thisClaim: number; before: number; balance: number; /** deposit paid by deposit claims to date (incl. this one) */ paid: number; /** this claim is the deposit itself */ isDeposit?: boolean };
  totalClaim: number;
  retention: { rate: number; thisClaim: number; limit: number; before: number; toDate: number; remaining: number };
  payableVep: number;
  vat: number;
  payableVip: number;
  toDate: ToDate; // including this claim
  balance: { contractSum: number; variationsToDate: number; contractValue: number; retention: number; deposit: number; paid: number; balance: number; /** deposit paid by deposit claims to date */ depositPaid: number };
};

/** Assess one side (PBS's claim or SWI's recommendation). `approvedVariations` = approved in the register (for the retention limit). */
export function assess(
  t: Terms,
  works: number,
  variations: number,
  before: ToDate,
  approvedVariations: number,
  override: { deposit?: number | null; retention?: number | null } = {},
): Assessment {
  const vatPct = t.vat_pct ?? 12.5;
  const totalVep = r2(works + variations);
  const depFull = n0(before.deposit_paid) > 0 ? r2(n0(before.deposit_paid)) : r2((n0(t.sum_vep) * n0(t.deposit_pct)) / 100);
  const depLeft = Math.max(0, r2(depFull - before.deposit_recovered));
  const depThis = typeof override.deposit === 'number' ? r2(override.deposit) : Math.min(depLeft, r2((depFull * n0(t.deposit_recovery_pct)) / 100));
  const totalClaim = r2(totalVep - depThis);
  const limit = r2(((n0(t.sum_vep) + approvedVariations) * n0(t.retention_limit_pct || t.retention_pct)) / 100);
  const retLeft = Math.max(0, r2(limit - before.retention_held));
  const retThis = typeof override.retention === 'number' ? r2(override.retention) : Math.min(retLeft, Math.max(0, r2((totalClaim * n0(t.retention_pct)) / 100)));
  const payableVep = r2(totalClaim - retThis);
  const toDate: ToDate = {
    gross_vep: r2(before.gross_vep + totalVep),
    variations_vep: r2(before.variations_vep + variations),
    deposit_recovered: r2(before.deposit_recovered + depThis),
    retention_held: r2(before.retention_held + retThis),
    paid_vep: r2(before.paid_vep + payableVep),
    deposit_paid: r2(n0(before.deposit_paid)),
  };
  return finish(t, r2(works), r2(variations), totalVep, { full: depFull, rate: n0(t.deposit_recovery_pct), thisClaim: depThis, before: before.deposit_recovered }, totalClaim, retThis, limit, before, payableVep, vatPct, toDate);
}

/** A deposit (advance payment) claim: the deposit amount, no works or recovery; retention only if `retention` is ticked. */
export function assessDeposit(
  t: Terms,
  amount: number,
  before: ToDate,
  approvedVariations: number,
  opt: { retention?: boolean; retentionOverride?: number | null } = {},
): Assessment {
  const vatPct = t.vat_pct ?? 12.5;
  const dep = r2(amount);
  const limit = r2(((n0(t.sum_vep) + approvedVariations) * n0(t.retention_limit_pct || t.retention_pct)) / 100);
  const retLeft = Math.max(0, r2(limit - before.retention_held));
  const retThis = !opt.retention ? 0 : typeof opt.retentionOverride === 'number' ? r2(opt.retentionOverride) : Math.min(retLeft, Math.max(0, r2((dep * n0(t.retention_pct)) / 100)));
  const payableVep = r2(dep - retThis);
  const toDate: ToDate = {
    gross_vep: r2(before.gross_vep),
    variations_vep: r2(before.variations_vep),
    deposit_recovered: r2(before.deposit_recovered),
    retention_held: r2(before.retention_held + retThis),
    paid_vep: r2(before.paid_vep + payableVep),
    deposit_paid: r2(n0(before.deposit_paid) + dep),
  };
  const a = finish(t, 0, 0, 0, { full: dep, rate: n0(t.deposit_recovery_pct), thisClaim: 0, before: before.deposit_recovered }, dep, retThis, limit, before, payableVep, vatPct, toDate);
  a.deposit.isDeposit = true;
  return a;
}

function finish(
  t: Terms,
  works: number,
  variations: number,
  totalVep: number,
  dep: { full: number; rate: number; thisClaim: number; before: number },
  totalClaim: number,
  retThis: number,
  limit: number,
  before: ToDate,
  payableVep: number,
  vatPct: number,
  toDate: ToDate,
): Assessment {
  const vat = r2((payableVep * vatPct) / 100);
  const contractValue = r2(n0(t.sum_vep) + toDate.variations_vep);
  const depPaid = n0(toDate.deposit_paid);
  // with a deposit claim on record, paid to date already holds the deposit — only recovery beyond it reduces the balance
  const depOff = depPaid > 0 ? Math.max(0, r2(toDate.deposit_recovered - depPaid)) : toDate.deposit_recovered;
  return {
    works,
    variations,
    totalVep,
    deposit: { ...dep, balance: r2(Math.max(dep.full, depPaid) - toDate.deposit_recovered), paid: depPaid },
    totalClaim,
    retention: { rate: n0(t.retention_pct), thisClaim: retThis, limit, before: before.retention_held, toDate: toDate.retention_held, remaining: r2(limit - toDate.retention_held) },
    payableVep,
    vat,
    payableVip: r2(payableVep + vat),
    toDate,
    balance: {
      contractSum: n0(t.sum_vep),
      variationsToDate: toDate.variations_vep,
      contractValue,
      retention: toDate.retention_held,
      deposit: depOff,
      paid: toDate.paid_vep,
      balance: r2(contractValue - toDate.retention_held - depOff - toDate.paid_vep),
      depositPaid: depPaid,
    },
  };
}

export const ZERO: ToDate = { gross_vep: 0, variations_vep: 0, deposit_recovered: 0, retention_held: 0, paid_vep: 0, deposit_paid: 0 };
export const addToDate = (a: ToDate, b: Partial<ToDate>): ToDate => ({
  gross_vep: r2(a.gross_vep + n0(b.gross_vep)),
  variations_vep: r2(a.variations_vep + n0(b.variations_vep)),
  deposit_recovered: r2(a.deposit_recovered + n0(b.deposit_recovered)),
  retention_held: r2(a.retention_held + n0(b.retention_held)),
  paid_vep: r2(a.paid_vep + n0(b.paid_vep)),
  deposit_paid: r2(n0(a.deposit_paid) + n0(b.deposit_paid)),
});

export const money = (v: number | null | undefined, opts: { brackets?: boolean } = {}) => {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  const s = Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 || opts.brackets ? `(${s})` : s;
};
export const pct = (v: number) => `${Math.round(v * 100) / 100} %`;
